import React, { useMemo, useState } from 'react';
import orderBy from 'lodash/orderBy';
import type { ZodRawShape } from 'zod';
import { z } from 'zod';
import { TaxonomyConst } from '@axiom/const';
import type {
  Candidate,
  CandidateLegalTechSkill,
  Taxonomy,
} from '@axiom/validation';
import {
  AccordionsItem,
  AccordionsItemHeader,
  Accordions,
  AccordionsItemSection,
  Button,
  CondensedHeader,
  CondensedLarge,
  Form,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ParagraphHeader,
  Radio,
  useBreakpoint,
} from '@axiom/ui';

import { CandidateLegalTechSkillsApi } from '../../api/candidate-legal-tech-skills-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';

export const LegalTechSkillsEdit: React.FC<{
  candidateId: Candidate['id'];
  closeModal: () => void;
  legalTechSkills: CandidateLegalTechSkill[];
  taxonomy: Taxonomy;
}> = ({ candidateId, closeModal, legalTechSkills, taxonomy }) => {
  const { isMobile } = useBreakpoint();
  const orderedLegalTechSkillsStructure = useMemo(() => {
    return orderBy(
      Object.entries(taxonomy.legalTechSkillCategories).map(
        ([key, { displayName }]) => ({
          key,
          displayName,
          sort: key.toLowerCase(),
          skills: orderBy(
            Object.entries(taxonomy.legalTechSkills)
              .filter(([, skill]) => skill.legalTechSkillCategory === key)
              .map(([skillKey, skill]) => ({
                key: skillKey,
                sort: skillKey.toLowerCase(),
                ...skill,
              })),
            ['sort']
          ),
        })
      ),
      ['sort']
    );
  }, [taxonomy]);

  type NestedMappingInput<T> = T | ((skill: string, category: string) => T);

  const returnNestedMapping = (dataOrFunc: NestedMappingInput<unknown>) =>
    orderedLegalTechSkillsStructure.reduce(
      (schema, { key, skills }) => ({
        ...schema,
        [key]: skills.reduce(
          (acc, skill) => ({
            ...acc,
            [skill.key]:
              typeof dataOrFunc === 'function'
                ? dataOrFunc(skill.key, key)
                : dataOrFunc,
          }),
          {}
        ),
      }),
      {}
    );

  const initialValues = useMemo(
    () =>
      returnNestedMapping(
        (skill: string, category: string) =>
          legalTechSkills.find(
            ({ legalTechSkill, legalTechCategory }) =>
              legalTechSkill === skill && legalTechCategory === category
          )?.proficiency ?? TaxonomyConst.LegalTechSkillsProficiencies.None
      ),
    [orderedLegalTechSkillsStructure, legalTechSkills]
  );
  const formSchema = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          Object.entries(returnNestedMapping(z.string())).map(
            ([key, value]) => [key, z.object(value as ZodRawShape)]
          )
        )
      ),
    [orderedLegalTechSkillsStructure]
  );
  const candidateLegalTechSkillsLookup = useMemo(
    () =>
      legalTechSkills.reduce<
        Record<string, Record<string, CandidateLegalTechSkill>>
      >((acc, curr) => {
        const legalCategory = curr.legalTechCategory;
        const legalSkill = curr.legalTechSkill;
        if (legalCategory && legalSkill) {
          return {
            ...acc,
            [legalCategory]: {
              ...acc[legalCategory],
              [legalSkill]: curr,
            },
          };
        }

        return acc;
      }, {}),
    [legalTechSkills]
  );
  const [openAccordion, setOpenAccordion] = useState<string | null>();
  return (
    <Form
      name="LEGAL_TECH_SKILLS_EDIT_FORM"
      schema={formSchema}
      initialValues={initialValues}
      onSubmit={async changedData => {
        const [updates, deletes] = Object.entries({
          ...initialValues,
          ...changedData,
        }).reduce(
          ([updateList, deleteList], [legalTechCategory, skills]) => {
            Object.entries(skills || {}).forEach(
              ([legalTechSkill, proficiency]) => {
                if (
                  proficiency !==
                  TaxonomyConst.LegalTechSkillsProficiencies.None
                ) {
                  // we PUT all values that are not none
                  // @ts-expect-error Needs type fix with APCORE-2687
                  updateList.push({
                    legalTechCategory,
                    legalTechSkill,
                    proficiency,
                  });
                } else if (
                  candidateLegalTechSkillsLookup[legalTechCategory]?.[
                    legalTechSkill
                  ]?.id
                ) {
                  deleteList.push(
                    candidateLegalTechSkillsLookup[legalTechCategory][
                      legalTechSkill
                    ].id
                  );
                }
              }
            );
            return [updateList, deleteList];
          },
          [
            [] as CandidateLegalTechSkill[],
            [] as Array<CandidateLegalTechSkill['id']>,
          ]
        );
        if (deletes.length > 0) {
          await CandidateLegalTechSkillsApi.deleteLegalTechSkills(
            candidateId,
            deletes
          );
        }
        // if there is an actual update to make, i.e. not a noop for skills with proficiencies
        if (
          updates.some(
            ({ legalTechSkill, legalTechCategory, proficiency }) =>
              // either a candidate skill record does not exist, so we need to create
              !candidateLegalTechSkillsLookup[legalTechCategory as string]?.[
                legalTechSkill as string
              ]?.proficiency ||
              // or it does exist with a different proficiency, so we need to update
              candidateLegalTechSkillsLookup[legalTechCategory as string]?.[
                legalTechSkill as string
              ]?.proficiency !== proficiency
          )
        ) {
          await CandidateLegalTechSkillsApi.updateLegalTechSkills(
            candidateId,
            updates
          );
        }
        if (Object.keys(changedData).length > 0) {
          await CandidateLegalTechSkillsApi.refreshLegalTechSkills(candidateId);
          await CandidateProfileIssuesApi.refreshCandidateProfileIssues(
            candidateId
          );
        }
        closeModal();
      }}
    >
      {({ fireSubmit }) => (
        <Modal name="LEGAL_TECH_SKILLS_MODAL">
          <ModalHeader
            onClose={() => {
              closeModal();
            }}
            name="LEGAL_TECH_SKILLS_MODAL_HEADER"
          >
            Legal Tech Skills
          </ModalHeader>
          <ModalSection>
            <ParagraphHeader>
              Select your proficiency level for each of the following Legal Tech
              skills.
            </ParagraphHeader>
            <Gutter bottom="16px" />
            <Accordions name="LEGAL_TECH_SKILLS_ACCORDIONS">
              {orderedLegalTechSkillsStructure.map(
                ({
                  key: categoryKey,
                  displayName: categoryDisplayName,
                  skills,
                }) => (
                  <AccordionsItem
                    key={categoryKey}
                    name={categoryKey}
                    expanded={openAccordion === categoryKey}
                  >
                    <AccordionsItemHeader
                      onToggle={value => {
                        setOpenAccordion(value ? categoryKey : null);
                      }}
                    >
                      <CondensedHeader name="LEGAL_TECH_SKILL_CATEGORY_NAME_EDIT">
                        {categoryDisplayName}
                      </CondensedHeader>
                    </AccordionsItemHeader>
                    {skills.map(({ displayName, key }) => (
                      <AccordionsItemSection key={`${categoryKey}.${key}`}>
                        <Gutter bottom="24px" />
                        <Layout
                          position="space-between"
                          stackableOn="mobile"
                          verticalGutter="8px"
                        >
                          <LayoutItem fluid>
                            <CondensedLarge
                              name={`LEGAL_TECH_SKILL_${categoryKey}_${key}`}
                            >
                              {displayName}
                            </CondensedLarge>
                          </LayoutItem>
                          <LayoutItem>
                            <Layout
                              horizontalGutter={isMobile ? '8px' : '24px'}
                            >
                              {TaxonomyConst.LegalTechSkillsProficienciesOptions.map(
                                option => (
                                  <Radio
                                    key={`${categoryKey}.${key}.${option.value}`}
                                    name={`${categoryKey}.${key}`}
                                    option={option.value}
                                    displayValue={option.label}
                                  />
                                )
                              )}
                            </Layout>
                          </LayoutItem>
                        </Layout>
                      </AccordionsItemSection>
                    ))}
                  </AccordionsItem>
                )
              )}
            </Accordions>
            <Gutter bottom="16px" />
            <CondensedLarge name="LEGAL_TECH_SKILLS_FEEDBACK">
              Do you have suggestions for additional skills to add? Send
              feedback to{' '}
              <a
                data-test="LEGAL_TECH_SKILLS_FEEDBACK_MAILTO"
                href="mailto:skills-feedback@axiomlaw.com?subject=Legal tech skills suggestions&body=Dear Axiom Product Team,%0D%0A%0D%0AI'd like to recommend that Axiom add the following legal tech skills:"
              >
                skills-feedback@axiomlaw.com
              </a>
            </CondensedLarge>
          </ModalSection>
          <ModalFooter>
            <Button
              variation="outline"
              onClick={() => {
                closeModal();
              }}
              name="PENDO_LEGAL_TECH_SKILLS_EDIT_CANCEL"
            >
              Cancel
            </Button>
            <Button
              onClick={fireSubmit}
              name="PENDO_LEGAL_TECH_SKILLS_EDIT_SAVE"
            >
              Save
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
